import { useEffect, useState } from "react";
import { Server, Users, CheckCircle2, Monitor, Clock, RefreshCw } from "lucide-react";
import { PageHeader, Card, Stat, Badge, Spinner, Empty, ErrorNote } from "../components/ui";

const API = "https://cadence-api.lightspeedphotonics.com/api/cadence-license";

export default function CadenceLicense() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState("");

  const fetchData = async () => {
    try {
      const res = await fetch(API);
      if (!res.ok) throw new Error();
      setData(await res.json());
      setError("");
      setLastRefresh(new Date().toLocaleTimeString());
    } catch {
      setError("Could not reach the licence server. Showing the last known data if available.");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <Spinner label="Loading licence status" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cadence License Monitor"
        subtitle="Live licence status, refreshed every 10 seconds."
        actions={
          data && (
            <>
              <Badge tone={data.serverStatus === "ONLINE" ? "good" : "bad"}>
                <Server size={12} /> {data.serverStatus}
              </Badge>
              <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                <RefreshCw size={12} /> {lastRefresh}
              </span>
            </>
          )
        }
      />

      <ErrorNote>{error}</ErrorNote>

      {data && (
        <>
          <div className="grid sm:grid-cols-3 gap-5">
            <Stat label="Total licences" value={data.summary.totalLicenses} icon={<Users size={18} />} />
            <Stat label="In use" value={data.summary.licensesInUse} tone="brand" icon={<Monitor size={18} />} />
            <Stat label="Available" value={data.summary.licensesAvailable} tone="good" icon={<CheckCircle2 size={18} />} />
          </div>

          <Card title="Active users" padded={false}>
            {data.activeUsers.length === 0 ? (
              <Empty>No licences are currently in use.</Empty>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
                      <th className="px-5 py-3 font-medium">User</th>
                      <th className="px-5 py-3 font-medium">Computer</th>
                      <th className="px-5 py-3 font-medium">Started</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.activeUsers.map((u, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-5 py-3.5 font-medium text-[#111111]">{u.username}</td>
                        <td className="px-5 py-3.5 text-gray-700">
                          <span className="inline-flex items-center gap-2"><Monitor size={14} className="text-gray-400" />{u.machine}</span>
                        </td>
                        <td className="px-5 py-3.5 text-gray-700">
                          <span className="inline-flex items-center gap-2"><Clock size={14} className="text-gray-400" />{u.start}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
